import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import i18n, { ensureLang } from "@/i18n";

type Lang = "nl" | "fr" | "en";
type Device = "desktop" | "mobile";

const OG_V = "?v=6";

/** Interne controlelijst: pad → seo-namespace key → social-share afbeelding. */
const PAGES: { path: string; seoKey: string; image: string }[] = [
  { path: "/", seoKey: "home", image: `/og-image.jpg${OG_V}` },
  { path: "/second-opinion", seoKey: "second-opinion", image: `/og-image.jpg${OG_V}` },
  { path: "/pakketten", seoKey: "pakketten", image: `/og/pakketten.jpg${OG_V}` },
  { path: "/diensten", seoKey: "diensten", image: `/og/diensten.jpg${OG_V}` },
  { path: "/audit", seoKey: "audit", image: `/og/audit.jpg${OG_V}` },
  { path: "/analyse", seoKey: "analyse", image: `/og/analyse.jpg${OG_V}` },
  { path: "/expertise", seoKey: "expertise", image: `/og/expertise.jpg${OG_V}` },
  { path: "/methode", seoKey: "methode", image: `/og/methode.jpg${OG_V}` },
  { path: "/proces", seoKey: "proces", image: `/og/proces.jpg${OG_V}` },
  { path: "/over-ons", seoKey: "over-ons", image: `/og/over-ons.jpg${OG_V}` },
  { path: "/technologie", seoKey: "technologie", image: `/og/technologie.jpg${OG_V}` },
  { path: "/contact", seoKey: "contact", image: `/og/contact.jpg${OG_V}` },
  {
    path: "/blog/wat-kost-een-website-laten-maken",
    seoKey: "blog-wat-kost",
    image: `/og/blog-wat-kost.jpg${OG_V}`,
  },
];

export const Route = createFileRoute("/og-preview")({
  head: () => ({
    meta: [
      { title: "Social preview check — IT Solutions" },
      { name: "description", content: "Interne controle van OG-afbeeldingen en link previews." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: OgPreviewPage,
});

function useSeoTexts(lang: Lang, ready: number) {
  return useMemo(() => {
    const t = i18n.getFixedT(lang, "seo");
    return PAGES.map((p) => ({
      ...p,
      title: t(`${p.seoKey}.title`) as unknown as string,
      description: t(`${p.seoKey}.description`) as unknown as string,
    }));
  }, [lang, ready]);
}

function Counter({ value, max, label }: { value: number; max: number; label: string }) {
  const over = value > max;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[0.7rem] font-semibold ${
        over ? "border-destructive text-destructive" : "border-border text-muted-foreground"
      }`}
    >
      {label} {value}/{max}
    </span>
  );
}

/** LinkedIn-achtige kaart: grote afbeelding, titel, domein. */
function LinkedInCard({
  title,
  description,
  image,
  device,
}: {
  title: string;
  description: string;
  image: string;
  device: Device;
}) {
  return (
    <div
      className="overflow-hidden rounded-sm border border-border bg-surface/30"
      style={{ maxWidth: device === "mobile" ? 360 : 552 }}
    >
      <img src={image} alt="" width={1200} height={630} className="block w-full" loading="lazy" />
      <div className="border-t border-border bg-background px-3 py-2">
        <p
          className={`font-semibold leading-snug ${device === "mobile" ? "text-sm" : "text-base"}`}
        >
          {title}
        </p>
        <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
          {description}
        </p>
        <p className="mt-1 text-[0.7rem] uppercase tracking-wide text-muted-foreground">
          itsolutions.be
        </p>
      </div>
    </div>
  );
}

/** Google-achtig zoekresultaat: pad, titel, snippet. */
function GoogleCard({
  title,
  description,
  path,
  device,
}: {
  title: string;
  description: string;
  path: string;
  device: Device;
}) {
  return (
    <div
      className="rounded-sm border border-border bg-background p-4"
      style={{ maxWidth: device === "mobile" ? 360 : 600 }}
    >
      <p className="text-xs text-muted-foreground">
        itsolutions.be{path === "/" ? "" : path.replace(/\//g, " › ")}
      </p>
      <p
        className={`mt-1 leading-snug text-brand-ink ${device === "mobile" ? "text-base" : "text-lg"}`}
      >
        {title.length > 60 ? `${title.slice(0, 57)}…` : title}
      </p>
      <p className="mt-1 text-sm leading-snug text-muted-foreground">
        {description.length > 160 ? `${description.slice(0, 157)}…` : description}
      </p>
    </div>
  );
}

function OgPreviewPage() {
  const [lang, setLang] = useState<Lang>("nl");
  const [ready, setReady] = useState(0);
  const [device, setDevice] = useState<Device>("desktop");
  // Talen worden per stuk geladen; deze previewpagina toont ze door elkaar.
  useEffect(() => {
    void ensureLang(lang).then(() => setReady((n) => n + 1));
  }, [lang]);
  const pages = useSeoTexts(lang, ready);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4">
          <h1 className="font-display text-lg font-semibold">Social preview check</h1>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex gap-1">
              {(["nl", "fr", "en"] as Lang[]).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`rounded-sm border px-3 py-1 text-xs font-semibold uppercase ${
                    lang === l
                      ? "border-brand bg-brand text-background"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              {(["desktop", "mobile"] as Device[]).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDevice(d)}
                  className={`rounded-sm border px-3 py-1 text-xs font-semibold ${
                    device === d
                      ? "border-brand bg-brand text-background"
                      : "border-border text-muted-foreground"
                  }`}
                >
                  {d === "desktop" ? "Desktop" : "Mobiel"}
                </button>
              ))}
            </div>
            <a href="/" className="text-sm text-muted-foreground hover:text-foreground">
              ← Terug naar de site
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10">
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Interne pagina. Per route ziet u de LinkedIn-kaart en het Google-resultaat zoals bezoekers
          die krijgen, met een teller op de lengte van titel (max 60 tekens) en omschrijving (max
          160 tekens).
        </p>

        <div className="mt-10 space-y-12">
          {pages.map((p) => (
            <section key={p.path} className="border-t border-border pt-8">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-xl font-semibold">{p.path}</h2>
                <Counter value={p.title.length} max={60} label="Titel" />
                <Counter value={p.description.length} max={160} label="Omschrijving" />
              </div>
              <div className="mt-6 flex flex-wrap items-start gap-8">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand">
                    LinkedIn
                  </p>
                  <LinkedInCard
                    title={p.title}
                    description={p.description}
                    image={p.image}
                    device={device}
                  />
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand">
                    Google
                  </p>
                  <GoogleCard
                    title={p.title}
                    description={p.description}
                    path={p.path}
                    device={device}
                  />
                </div>
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
